<script setup>
import { LoaderIcon, PlusIcon, XIcon } from "lucide-vue-next";
import { computed, inject, ref, watch } from "vue";

import SimpleDescription from "@/Components/SimpleDescription.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  },
  hideDeleteForUrls: {
    type: Array,
    default: () => ["ui-avatars.com"]
  },
  classes: {
    type: String,
    default: ""
  },
  previewClass: {
    type: String,
    default: ""
  },
  help: {
    type: String,
    default: ""
  },
  maxSize: {
    type: [Number, String],
    default: 1024
  },
  description: {
    type: String,
    default: ""
  },
  width: {
    type: [Number, String],
    default: 70
  },
  height: {
    type: [Number, String],
    default: 70
  },
  acceptedTypes: {
    type: Array,
    default: () => ["image/jpeg", "image/png", "image/gif"]
  },
  quality: {
    type: Number,
    default: 0.8
  }
});

const emit = defineEmits(["update:modelValue", "selected", "error", "clear"]);

const twMerge = inject("twMerge");

const inputRef = ref(null);
const photoPreview = ref(null);
const isProcessing = ref(false);
const error = ref(null);

const defaultAvatarClass = "avatar relative mt-1.5 cursor-pointer";
const defaultPreviewClass = "mask is-squircle group relative bg-100 p-8";
const computedAvatarClass = computed(() => twMerge(defaultAvatarClass, props.classes));
const computedPreviewClass = computed(() => twMerge(defaultPreviewClass, props.previewClass));

watch(
  () => props.modelValue,
  (newValue) => {
    photoPreview.value = newValue;
  }
);

const validateImage = (file) => {
  if (!props?.acceptedTypes?.includes(file.type)) {
    emit("error", "지원하지 않는 파일 형식입니다.");
    return false;
  }

  if (file.size > Number(props.maxSize) * 1024) {
    emit("error", `파일 크기가 ${props.maxSize}KB를 초과했습니다.`);
    return false;
  }

  return true;
};

const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Image compression failed"));
        },
        "image/jpeg",
        props.quality
      );
    };
    img.onerror = () => reject(new Error("Image loading failed"));
  });
};

const loadPhoto = async (file) => {
  try {
    isProcessing.value = true;
    error.value = null;

    if (!validateImage(file)) return;

    const compressedImage = await compressImage(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      photoPreview.value = result;

      emit("update:modelValue", result);
      emit("selected", compressedImage);
    };

    reader.onerror = () => {
      throw new Error("파일을 읽는 중 오류가 발생했습니다.");
    };

    reader.readAsDataURL(compressedImage);
  } catch (e) {
    error.value = e instanceof Error ? e.message : "알 수 없는 오류가 발생했습니다.";
    emit("error", error.value);
  } finally {
    isProcessing.value = false;
  }
};

const changed = (e) => {
  const target = e.target;
  const file = target.files?.[0];
  if (file) loadPhoto(file);
};

const triggerFileInput = () => {
  if (!isProcessing.value) {
    inputRef.value?.click();
  }
};

const clearImage = () => {
  photoPreview.value = null;
  emit("update:modelValue", "");
  emit("clear");
  if (inputRef.value) inputRef.value.value = "";
};
</script>

<template>
  <div>
    <div :class="computedAvatarClass" role="button" :aria-label="'프로필 이미지 선택'" @click="triggerFileInput">
      <div
        class="flex-center group absolute inset-0 z-0"
        :class="[computedPreviewClass, { 'opacity-50': isProcessing }]"
        :style="{
          width: props.width + 'px',
          height: props.height + 'px',
          backgroundImage: photoPreview ? `url(${photoPreview})` : props.modelValue ? `url(${props.modelValue})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }"
      >
        <div
          v-if="!photoPreview && !props.modelValue"
          class="relative z-1 block cursor-pointer text-center text-sm text-400 group-hover:text-zinc-700 dark:group-hover:text-zinc-300"
        >
          <template v-if="isProcessing">
            <LoaderIcon class="mx-auto h-4 w-4 animate-spin" />
            <span class="text-xs font-bold">처리중...</span>
          </template>
          <template v-else>
            <PlusIcon aria-hidden="true" class="mx-auto h-4 w-4" />
            <span class="text-xs font-bold">업로드</span>
            <small v-if="help" class="block" v-text="help"></small>
          </template>
        </div>

        <input ref="inputRef" type="file" class="hidden" :accept="props.acceptedTypes.join(',')" @change="changed" aria-label="이미지 파일 선택" />
      </div>

      <button
        v-if="(photoPreview || props.modelValue) && !props.hideDeleteForUrls.some((url) => props.modelValue?.includes(url))"
        class="btn absolute bottom-0 right-0 z-2 h-5 w-5 rounded-full bg-rose-600 p-0 transition text-600 hover:scale-110"
        :disabled="isProcessing"
        aria-label="이미지 제거"
        @click.stop="clearImage"
      >
        <XIcon class="h-3.5 w-3.5 text-white" />
      </button>

      <div v-if="error" class="absolute -bottom-6 left-0 right-0 text-center text-xs text-red-500" role="alert">
        {{ error }}
      </div>
    </div>

    <SimpleDescription v-if="description" classes="text-xs py-1 text-400 mt-4">
      {{ description }}
    </SimpleDescription>
  </div>
</template>
